/**
 * Fraction of a region the reader has moved through.
 * `start` and `end` are document scroll offsets. The value is clamped to 0–1.
 *
 * @param {number} scrollY
 * @param {number} start
 * @param {number} end
 * @returns {number}
 */
export function readingProgress(scrollY, start, end) {
  const span = end - start

  if (!(span > 0)) return 0

  const value = (scrollY - start) / span

  if (value <= 0) return 0
  if (value >= 1) return 1

  return value
}

/**
 * Keep the bar on the header's bottom edge while the announcement hides.
 *
 * @param {HTMLElement} bar
 */
function syncReadingProgressTop(bar) {
  const header = document.getElementById('header-group')

  if (!header) {
    bar.style.top = 'var(--header-group-height, 0px)'
    return
  }

  const distance =
    header.style.getPropertyValue('--header-announcement-hide-distance') ||
    '0px'
  const collapsed =
    header.getAttribute('data-announcement-state') === 'hidden' ||
    header.getAttribute('data-mega-menu-open') === 'true'
  const offset = collapsed ? `calc(${distance.trim()} * -1)` : '0px'

  bar.style.top = `calc(var(--header-group-height, 0px) + ${offset})`
}

/**
 * Paint the article reading bar from the current scroll position.
 */
export function updateArticleReadingProgress() {
  const region = document.querySelector('[data-article-reading]')
  const bar = document.querySelector('[data-article-reading-progress]')
  const fill = document.querySelector('[data-article-reading-fill]')

  if (
    !(region instanceof HTMLElement) ||
    !(bar instanceof HTMLElement) ||
    !(fill instanceof HTMLElement)
  ) {
    return
  }

  const scrollY = window.scrollY || window.pageYOffset || 0
  const rect = region.getBoundingClientRect()
  const top = rect.top + scrollY
  const bottom = rect.bottom + scrollY
  const viewport = window.innerHeight || 0
  const end = Math.max(bottom - viewport, top)
  const progress = readingProgress(scrollY, top, end)
  const percent = Math.round(progress * 100)

  fill.style.transform = `scaleX(${progress})`
  bar.setAttribute('aria-valuenow', String(percent))
  syncReadingProgressTop(bar)
}

/**
 * Track scroll through `[data-article-reading]`.
 */
export function initArticleReadingProgress() {
  const bar = document.querySelector('[data-article-reading-progress]')

  if (!(bar instanceof HTMLElement)) return
  if (bar.dataset.articleReadingBound === 'true') return

  bar.dataset.articleReadingBound = 'true'

  let frame = 0

  const schedule = () => {
    if (frame) return

    frame = window.requestAnimationFrame(() => {
      frame = 0
      updateArticleReadingProgress()
    })
  }

  updateArticleReadingProgress()
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule)

  const header = document.getElementById('header-group')

  if (header) {
    const observer = new MutationObserver(schedule)
    observer.observe(header, {
      attributes: true,
      attributeFilter: [
        'style',
        'data-announcement-state',
        'data-mega-menu-open'
      ]
    })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initArticleReadingProgress)
} else {
  initArticleReadingProgress()
}
